import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import * as moment from "moment";
import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getAppUrl } from "../../global/GlobalTools";
// Context
import { MainContext } from "../../../App";
// API
import { GetNonWorkingDays } from "../../../api/class";

// Custom Styles
const customStyles = {
  holidaysContainer: {
    maxHeight: "50vh",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "thin",
  },
  holidayContainer: {
    borderRadius: "4px",
    padding: "8px",
    background: "#F6F6F6",
    border: "1px solid #D6DADB",
    cursor: "pointer",
  },
};

// Lazy Loading Component
const LoadingHolidays = () => (
  <Container id="lazyLoadingContainer" className="table-lazyLoading">
    {[...Array(3)].map((_, idx) => (
      <Row key={idx} id="lazyLoadingRow">
        <Col md={12} className="mt-2" id="lazyLoadingCol">
          <div id="lazyLoadingDiv" />
        </Col>
      </Row>
    ))}
  </Container>
);

const ModalHolidays = ({ show, onHide, schoolCycleId }) => {
  const [t] = useTranslation(["global", "class"]);
  const { permissions } = useContext(MainContext);
  const canCreateEvent = permissions.events_list?.create;
  const canEditEvent = permissions.events_list?.edit;

  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /* Initial loading  */
  useEffect(() => {
    getHolidays();
    // Listen to visibility change to know when to refresh holidays
    window.addEventListener("visibilitychange", refreshHolidays);
    // Cleanup listener to avoid calls when component is unmounted
    return () => {
      window.removeEventListener("visibilitychange", refreshHolidays);
    };
  }, []);

  /**
   * Description: Function to refresh holidays when document becomes visible
   */
  const refreshHolidays = () => {
    if (document.visibilityState === "visible") {
      getHolidays();
    }
  };

  /**
   * Description: Function to get holidays from API
   */
  const getHolidays = () => {
    const payload = {
      school_cycle_id: schoolCycleId,
    };
    setIsLoading(true);
    GetNonWorkingDays(payload)
      .then((result) => {
        if (result?.data) {
          const holidays = formatHolidays(result.data);
          setHolidays(holidays);
        }
        if (result.description) {
          setError(result.description);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  /**
   * Description: Function to format holidays data
   * @param {array} [holidays=[]]
   * @return {array}
   */
  const formatHolidays = (holidays = []) => {
    return holidays.map((holiday) => {
      const isUtc = holiday.is_google_event === "1";
      const startDateFormatted = formatDate(holiday.start_time, isUtc);
      const endDateFormatted = formatDate(holiday.end_time, isUtc);

      return {
        id: holiday.id,
        startDate: startDateFormatted,
        startTime: holiday.start_time,
        endDate: endDateFormatted,
        endTime: holiday.end_time,
        title: holiday.title,
        levelNames: holiday.school_levels_name,
        isEditable: canEditEvent && holiday.is_google_event !== "1",
        label: `${startDateFormatted} - ${endDateFormatted} · ${holiday.title}`,
      };
    });
  };

  /**
   * Description: Function to format timestamp to DD/MM/YYYY
   * @param {number} timestamp
   * @return {string}
   */
  const formatDate = (timestamp, isUtc) => {
    const momentObj = moment.unix(timestamp);
    const dateLocal = isUtc ? momentObj.utc() : momentObj.local();
    return dateLocal.format("DD/MM/YYYY");
  };

  /**
   * Description: Function to build base URL for events page
   * @return {string}
   */
  const baseURLEvents = () => {
    return getAppUrl(`/eventos?isHoliday=true&cycleId=${schoolCycleId}`, true);
  };

  /**
   * Description: Function to open create event page in a new tab
   */
  const createEvent = () => {
    const baseUrl = baseURLEvents();
    const queryParams = "&createEventModal=true";
    const url = baseUrl + queryParams;
    window.open(url, "_blank");
  };

  /**
   * Description: Function to open edit event page in a new tab
   * @param {object} holiday
   */
  const editEvent = (holiday) => {
    const baseUrl = baseURLEvents();
    const eventId = holiday.id;
    const queryParams = `&start=${holiday.startTime}&editEventId=${eventId}`;
    const url = baseUrl + queryParams;
    window.open(url, "_blank");
  };

  return (
    <Modal show={show} id="modalHolidays">
      <Modal.Header id="modalHolidaysHeader">
        <Modal.Title id="modalHolidaysTitle">
          {t("class:modalHolidays.title")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body id="modalHolidaysBody">
        {canCreateEvent && (
          <React.Fragment>
            {/* Holiday message */}
            <Row className="mb-3" id="holidaysMessageRow">
              <Col xs={12} id="holidaysMessageCol">
                {t("class:modalHolidays.addHolidaysMessage")}
              </Col>
            </Row>
            {/* Link to add holiday */}
            <Row className="mb-3" id="addHolidayRow">
              <Col xs={12} id="holidaysAddCol">
                <Button
                  variant="link"
                  id="addHolidaysBtn"
                  onClick={createEvent}
                >
                  {t("class:modalHolidays.addHoliday")}
                </Button>
              </Col>
            </Row>
          </React.Fragment>
        )}
        {/* Holidays List */}
        <div id="holidaysContainer" style={customStyles.holidaysContainer}>
          {isLoading ? (
            <LoadingHolidays />
          ) : (
            holidays.map((holiday, index) => (
              <Container
                key={holiday.id}
                id={`holidayContainer-${index}`}
                className="mb-3"
                onClick={holiday.isEditable ? () => editEvent(holiday) : null}
                style={{
                  ...customStyles.holidayContainer,
                  cursor: holiday.isEditable ? "pointer" : "default",
                }}
              >
                <Row id={`holidayRow-${index}`}>
                  <Col xs={12} className="fw-bold" id={`holidayTitle-${index}`}>
                    {holiday.label}
                  </Col>
                </Row>
                <Row id={`holidayLevelsRow-${index}`}>
                  <Col xs={12} id={`holidayLevels-${index}`}>
                    {holiday.levelNames}
                  </Col>
                </Row>
              </Container>
            ))
          )}
        </div>
        {/** Show error message */}
        {error && (
          <Row id="modalHolidaysErrorRow">
            <Col id="modalHolidaysErrorCol" md={12} className="mt-3">
              <Alert id="modalHolidaysError" variant="warning">
                {error}
              </Alert>
            </Col>
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer id="modalHolidaysFooter">
        <Button variant="secondary" onClick={onHide} id="holidaysCancelBtn">
          {t("global:buttons.cancel")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

ModalHolidays.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  schoolCycleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ModalHolidays;
